import { AsyncPipe, ViewportScroller } from '@angular/common';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BackNavigationService,
  menuItem,
  requestFn,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import { authorizationFn } from '@softline/auth';
import {
  isDefinedNotEmpty,
  isNullOrEmpty,
  Store,
  ValidationMessage,
} from '@softline/core';
import { FileInputComponent, InputComponent, MessageBarStore, ModalStore, MultilineInputComponent, NumberInputComponent, SOFTLINE_FEATURE_MESSAGE_BAR, SOFTLINE_FEATURE_MODAL, TranslatePipe, translator, ValidationContainerComponent, ValidationMessagePipe, Validators } from '@softline/ui-core';
import { SignatureInputComponent } from '@softline/ui-core/draw';
import { combineLatestWith, Observable, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AddItemDialogComponent } from '../../dialogs/add-item-dialog/add-item-dialog.component';
import { AuftragResourceService } from '../../services/auftrag-resource.service';
import { AuftragStore, StationStore } from '../../store';
import {
  SOFTLINE_PERMISSION_TRANSPORTAUFTRAG_SUBMIT_INVOICE,
  SOFTLINE_PERMISSION_TRANSPORTAUFTRAG_SUBMIT_LIEFERSCHEIN,
} from '../../transport.permissions';
import {
  SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
  SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
} from '../../transport.shared';
import { Adresse } from '../../types/adresse';
import { Artikel } from '../../types/artikel';
import { ArtikelEinheit } from '../../types/artikel-einheit';
import { Auftrag } from '../../types/auftrag';
import { BeweauBean } from '../../types/beweau-bean';
import { KopfauBean } from '../../types/kopfau-bean';
import { ListItemInputComponent } from './list-item-input/list-item-input.component';

@Component({
  selector: 'soft-tp-station-detail',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    ListItemInputComponent,
    FileInputComponent,
    InputComponent,
    MultilineInputComponent,
    NumberInputComponent,
    PageComponent,
    PageHeaderComponent,
    SignatureInputComponent,
    TranslatePipe,
    ValidationContainerComponent,
    ValidationMessagePipe,
  ],
  templateUrl: './station-detail.component.html',
  styleUrls: ['./station-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StationDetailComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly route = inject(ActivatedRoute);
  private readonly backNavigationService = inject(BackNavigationService);
  private readonly resourceService = inject(AuftragResourceService);

  private readonly t = translator();
  private readonly request = requestFn();

  private subscription?: Subscription;
  private formSubscription?: Subscription;
  private stationSubscription?: Subscription;
  private auftragSubscription?: Subscription;

  private readonly id$ = this.route.paramMap.pipe(map((o) => +(o.get('id') ?? 0)));

  private readonly stationId$ = this.route.paramMap.pipe(
    map((o) => +(o.get('stationId') ?? 0))
  );

  private readonly isAuthorized = authorizationFn();
  readonly hasRechnungPermission = this.isAuthorized(SOFTLINE_PERMISSION_TRANSPORTAUFTRAG_SUBMIT_INVOICE);
  readonly hasLieferscheinPermission = this.isAuthorized(SOFTLINE_PERMISSION_TRANSPORTAUFTRAG_SUBMIT_LIEFERSCHEIN);

  readonly auftrag$: Observable<Auftrag | null> = this.id$.pipe(
    switchMap((id) =>
      this.store.observe(
        SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
        AuftragStore.getters.entityWithDetail,
        id
      )
    )
  );

  readonly station$: Observable<(KopfauBean & { _original: KopfauBean }) | null> =
    this.stationId$.pipe(
      switchMap((id) =>
        this.store
          .observe(
            SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
            StationStore.getters.preparedEntity,
            id
          )
          .pipe(
            combineLatestWith(
              this.store.observe(
                SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
                StationStore.getters.entity,
                id
              )
            )
          )
      ),
      map(([kopfau, original]) => ({ ...kopfau, _original: original }))
    );

  saving = false;
  isCompleted$ = this.station$.pipe(map((o) => !!o?.idkopflf));

  readonly form = this.fb.group({
    belegversandemail: new UntypedFormControl('', {
      validators: [Validators.email()],
      updateOn: 'blur',
    }),
    lieferhinweis: [''],
    extLfnr: [''],
    files: [null],
    signature: [null],
  });

  readonly messages: ValidationMessage[] = [
    {
      type: 'error',
      message: 'Bitte überprüfen Sie das Format der Eingabe',
      subject: 'Keine gültige E-Mail',
    },
  ];

  readonly trackByFn = (index: number, item: BeweauBean) => item.artikel?.id ?? item;

  constructor() {
    menuItem(() => {
      const stationId = +(this.route.snapshot.paramMap.get('stationId') ?? 0);
      const station = this.store.get(
        SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
        StationStore.getters.preparedEntity,
        stationId,
      );
      const isCompleted = !!station?.idkopflf;

      return {
        title: isCompleted
          ? '#TRANSPORT.STATION_DETAIL.CLOSE'
          : '#TRANSPORT.STATION_DETAIL.SUBMIT',
        type: 'execute',
        canExecute: true,
        outlet: 'page-bottom',
        class: 'soft-button primary h-16 rounded mx-2 mb-1',
        name: isCompleted ? 'closeStation' : 'completeStation',
        execute: async () => {
          if (isCompleted) {
            await this.navigateBack();
            return;
          }
          const id = +(this.route.snapshot.paramMap.get('id') ?? 0);
          const auftrag = this.store.get(
            SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
            AuftragStore.getters.entityWithDetail,
            id,
          );
          const currentStation = this.store.get(
            SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
            StationStore.getters.preparedEntity,
            stationId,
          );
          if (auftrag && currentStation) {
            await this.onComplete(auftrag as Auftrag, currentStation);
          }
        },
      };
    });
  }

  ngOnInit(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
    const content = document?.getElementsByTagName('soft-pull-to-refresh-container');
    content[0].scrollTop = 0;

    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      'Energietransport'
    );
    this.auftragSubscription = this.station$
      .pipe(combineLatestWith(this.auftrag$))
      .subscribe(([station, auftrag]) => {
        this.store.commit(
          SOFTLINE_FEATURE_TITLE,
          TitleStore.mutations.setSubTitle,
          `Transportauftrag ${auftrag?.nummer}, Station ${station?.lfdnr}`
        );
      });

    this.subscription = this.id$.subscribe(async (id) => {
      if (!id) return;
      const entity = this.store.get(
        SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
        AuftragStore.getters.entity,
        id,
      );
      if (!entity) {
        await this.store.dispatch(
          SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
          AuftragStore.actions.loadMany,
          { clear: true },
        );
      }
      await this.store.dispatch(
        SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
        AuftragStore.actions.loadDetail,
        { id },
      );
    });

    this.stationSubscription = this.station$.subscribe((o) => {
      if (o) {
        this.form.patchValue(o, { emitEvent: false });
        this.form.markAllAsTouched();
      }
    });

    this.formSubscription = this.form.valueChanges.subscribe(async (value) => {
      const stationId = +(this.route.snapshot.paramMap.get('stationId') ?? 0);
      await this.patchStation(stationId, value);
    });
  }

  ngOnDestroy(): void {
    if (this.subscription && !this.subscription.closed)
      this.subscription.unsubscribe();
    this.subscription = undefined;
    if (this.formSubscription && !this.formSubscription.closed)
      this.formSubscription.unsubscribe();
    this.formSubscription = undefined;
    if (this.stationSubscription && !this.stationSubscription.closed)
      this.stationSubscription.unsubscribe();
    this.stationSubscription = undefined;
    if (this.auftragSubscription && !this.auftragSubscription.closed)
      this.auftragSubscription.unsubscribe();
    this.auftragSubscription = undefined;

    this.backNavigationService.set(undefined);
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, '');
  }

  async navigateBack(): Promise<void> {
    const id = +(this.route.snapshot.paramMap.get('id') ?? 0);
    await this.router.navigate(['/energietransport', id]);
  }

  async patchStation(stationId: number, changes: Partial<KopfauBean>): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
      StationStore.actions.preparePatch,
      { id: stationId, changes }
    );
  }

  async onComplete(auftrag: Auftrag, station: KopfauBean): Promise<void> {
    if (station.beweaus.length === 0) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.warning,
        '#TRANSPORT.STATION_DETAIL.ERROR_AMOUNT'
      );
      return;
    }

    const type = await this.getCompletionType(auftrag.id, station);
    if (!type) return;
    station = { ...station, type };

    this.saving = true;
    const saveResult = await this.request({
      options: {
        type: 'saving',
        savingMessage: this.t('#TRANSPORT.STATION_DETAIL.SAVING'),
        successMessage: this.t('Die Station wurde abgeschlossen!'),
      },
      request: async () => {
        try {
          await this.store.dispatch(
            SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
            StationStore.actions.complete,
            {
              id: auftrag.id,
              station,
            }
          );
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e);
          if (!msg.includes('Can not reset')) throw e;
        }
        return true;
      },
    });
    this.saving = false;
    if (saveResult) await this.navigateBack();
  }

  private async getCompletionType(
    auftragId: any,
    station: any
  ): Promise<'invoice' | 'deliveryNote' | null> {
    let saveMethod: 'invoice' | 'deliveryNote' | 'DISMISSED' | null = null;

    const result = await this.resourceService.getAbschlussTypen(auftragId, station);

    if (result.length === 1) {
      switch (result[0]) {
        case 'KREDITRECHNUNG':
          saveMethod = 'invoice';
          break;
        case 'LIEFERSCHEIN':
          saveMethod = 'deliveryNote';
          break;
      }
    } else if (result.length > 1) {
      const options = result.map((o) => {
        switch (o) {
          case 'KREDITRECHNUNG':
            return {
              value: 'invoice',
              label: '#TRANSPORT.DIALOGS.SELECT_VARIANT.INVOICE',
            };
          case 'LIEFERSCHEIN':
            return {
              value: 'deliveryNote',
              label: '#TRANSPORT.DIALOGS.SELECT_VARIANT.LIEFERSCHEIN',
            };
          default:
            return { value: o, label: o };
        }
      });
      saveMethod = await this.store.dispatch(
        SOFTLINE_FEATURE_MODAL,
        ModalStore.actions.choose<'invoice' | 'deliveryNote'>(),
        {
          title: '#TRANSPORT.DIALOGS.SELECT_VARIANT.TITLE',
          subtitle: '#TRANSPORT.DIALOGS.SELECT_VARIANT.INFO',
          options: options,
          dismiss: true,
        }
      );
      if (saveMethod === 'DISMISSED') saveMethod = null;
    }

    return saveMethod;
  }

  generateUrl(adresse: Adresse | undefined): string | undefined {
    if (!adresse) return undefined;

    let destination = '';

    adresse.strasse = (adresse?.strasse ?? '')
      .replace('oe', 'ö')
      .replace('OE', 'Ö')
      .replace('ue', 'ü')
      .replace('UE', 'Ü')
      .replace('ae', 'ä')
      .replace('AE', 'Ä');

    if (adresse?.strasse) destination += encodeURIComponent(adresse?.strasse);

    if (adresse?.postleitzahl)
      destination += encodeURIComponent(
        destination?.length > 0 ? ', ' + adresse?.postleitzahl : adresse?.postleitzahl
      );
    if (adresse?.ort)
      destination += encodeURIComponent(
        destination?.length > 0 ? ' ' + adresse?.ort : adresse?.ort
      );

    return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
  }

  async onAddItem(station: KopfauBean): Promise<void> {
    const result: BeweauBean[] | null | 'DISMISSED' = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<
        | {
            artikel: Artikel;
            arteh: ArtikelEinheit;
            mengeVorschlag: number | null;
          }[]
        | null,
        any
      >(),
      {
        id: 'TRANSPORT_ADD_ITEM',
        component: AddItemDialogComponent,
        dismiss: true,
      }
    );
    if (result === 'DISMISSED') return;
    await this.insertItems(station, result);
  }

  private async insertItems(
    station: KopfauBean,
    items: BeweauBean[] | null
  ): Promise<void> {
    if (!items || items?.length < 1) return;
    items = items.filter((o) =>
      station.beweaus.every((p) => p.artikel.id !== o.artikel.id)
    );
    const beweaus = [...station.beweaus, ...items];
    await this.patchStation(station.id, { beweaus });
  }

  async quantityChanged(
    station: KopfauBean,
    item: BeweauBean,
    menge: number
  ): Promise<void> {
    const itemIndex = station.beweaus?.findIndex(
      ({ artikel, id }) => id === item.id && artikel.id === item.artikel.id
    );
    if (itemIndex < 0) return;
    const beweaus = [...station.beweaus];
    beweaus[itemIndex] = { ...beweaus[itemIndex], menge };
    await this.patchStation(station.id, { beweaus });
  }

  filterEmpty(item: any): boolean {
    return !isNullOrEmpty(item);
  }

  async removeItem(station: KopfauBean, item: any): Promise<void> {
    const itemIndex = station.beweaus?.findIndex(
      ({ artikel, id }) => id === undefined && artikel.id === item.artikel.id
    );
    if (itemIndex < 0) return;

    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.ask,
      '#TRANSPORT.DIALOGS.ASK.CONFIRM_REMOVE_ITEM'
    );
    if (result !== 'YES') return;
    const beweaus = [...station.beweaus];
    beweaus.splice(itemIndex, 1);
    await this.patchStation(station.id, { beweaus });
  }

  protected readonly isDefinedNotEmpty = isDefinedNotEmpty;
  protected readonly isNullOrEmpty = isNullOrEmpty;
}
